// Shared in-flight + TTL cache for the engagement-tracks GET endpoint.
//
// Three different callers (App.js, NextSteps, analytics-send) all fetch
// the same `/v2/engagement/tracks/?game=…&course=…&user=…&round_number=…`
// URL on certain page loads. Each previously had its own ad-hoc cache
// (instance field, window global), but none of them shared state, so
// the three concurrent on-mount calls all hit the network independently.
//
// This module exposes a single in-process cache:
//   - `fetchTracks(...)` returns a cached Promise when a request for
//     the same (game, course, user, round) tuple is in-flight or was
//     resolved within TTL_MS. Multiple concurrent callers receive the
//     same Promise reference, so the network sees one round trip.
//   - `invalidateTracks()` clears the cache. POST sites that record a
//     new page view call this so the next read picks up the new entry.
//
// TTL is intentionally short — long enough to dedupe concurrent mount
// fetches and rapid re-fetches, short enough that drift between client
// and server stays bounded. Any mutation (POST) invalidates immediately.

import { getRequest } from '../api/ApiService';

const TTL_MS = 30000;

// Module-scoped Map: key → { promise, resolvedAt | null }
// `resolvedAt = null` while a request is in flight; set to Date.now()
// once the network call settles. On rejection, the entry is removed so
// the next caller retries instead of latching onto a stale error.
const cache = new Map();

function buildKey(gameId, courseId, userId, roundNumber) {
  const round = roundNumber == null ? 'all' : roundNumber;
  return `${gameId}-${courseId}-${userId}-${round}`;
}

function buildUrl(gameId, courseId, userId, roundNumber) {
  const base = `${process.env.REACT_APP_API_URL}v2/engagement/tracks/?game=${gameId}&course=${courseId}&user=${userId}`;
  return roundNumber == null ? base : `${base}&round_number=${roundNumber}`;
}

export function fetchTracks({ gameId, courseId, userId, roundNumber }) {
  const key = buildKey(gameId, courseId, userId, roundNumber);
  const entry = cache.get(key);
  const now = Date.now();

  if (entry) {
    const inFlight = entry.resolvedAt == null;
    const fresh = entry.resolvedAt != null && (now - entry.resolvedAt) < TTL_MS;
    if (inFlight || fresh) {
      return entry.promise;
    }
  }

  const promise = getRequest(buildUrl(gameId, courseId, userId, roundNumber))
    .then((res) => {
      const current = cache.get(key);
      if (current && current.promise === promise) {
        current.resolvedAt = Date.now();
      }
      return res;
    })
    .catch((err) => {
      const current = cache.get(key);
      if (current && current.promise === promise) {
        cache.delete(key);
      }
      throw err;
    });

  cache.set(key, { promise, resolvedAt: null });
  return promise;
}

// Clear the entire tracks cache. Called by any code path that POSTs a
// new tracks entry — the next fetch must hit the network so the new
// page view is reflected in the result set.
export function invalidateTracks() {
  cache.clear();
}



// WEBPACK FOOTER //
// ./src/services/tracks-cache.js